import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  X, Compass, Target, Rocket, Star, Zap, Trophy, Calendar as CalendarIcon,
  Lock, CheckCircle2, Circle, Gauge,
} from "lucide-react";
import PerformanceGaps from "@/components/gps/PerformanceGaps";

const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

/**
 * Read-only GPS viewer for Coach / Admin.
 * Loads `/api/gps/player/{playerId}` and respects the consultant's privacy toggle.
 */
export default function GPSViewerModal({ playerId, playerName, onClose }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("135");

  useEffect(() => {
    if (!playerId) return;
    setData(null); setErr(null);
    api.get(`/gps/player/${playerId}?year=${year}`)
      .then((r) => setData(r.data))
      .catch((e) => { setErr(formatApiError(e)); toast.error(formatApiError(e)); });
  }, [playerId, year]);

  if (!playerId) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gps-viewer-modal">
      <div className="kw-card w-full max-w-5xl max-h-[95vh] flex flex-col relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-[#A3A3A3] hover:text-white z-10"
          data-testid="gps-viewer-close"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-5 border-b border-[#222] flex-shrink-0">
          <div className="kw-overline flex items-center gap-2">
            <Compass className="w-3 h-3 text-[#E50000]" /> KW Growth Planning System · Vista Secretário
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            GPS de <span className="text-[#E50000]">{playerName}</span>
          </h2>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => setYear(year - 1)} className="text-xs kw-btn-secondary !py-1 !px-2" data-testid="gps-viewer-year-prev">&lt;</button>
            <div className="font-display font-black text-lg min-w-[60px] text-center">{year}</div>
            <button onClick={() => setYear(year + 1)} className="text-xs kw-btn-secondary !py-1 !px-2" data-testid="gps-viewer-year-next">&gt;</button>
          </div>
        </div>

        {err && <div className="p-5 text-[#FF6B6B]" data-testid="gps-viewer-error">{err}</div>}
        {!data && !err && <div className="p-5 kw-overline">A carregar…</div>}

        {data && !data.shared && (
          <div className="p-8 text-center" data-testid="gps-viewer-private">
            <Lock className="w-10 h-10 text-[#737373] mx-auto mb-3" />
            <h3 className="font-display text-xl font-black uppercase">GPS Privado</h3>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-md mx-auto">
              {playerName} optou por manter o GPS privado. Só o consultor e o admin têm acesso
              a estes dados. Podes falar diretamente com o consultor para partilhar.
            </p>
          </div>
        )}

        {data && data.shared && (
          <>
            <div className="px-5 pt-4 flex gap-2 border-b border-[#222] flex-shrink-0">
              <button
                onClick={() => setTab("135")}
                data-testid="gps-viewer-tab-135"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
                  tab === "135" ? "border-[#E50000] text-white" : "border-transparent text-[#737373] hover:text-white"
                }`}
              >
                <Target className="w-4 h-4" /> 1-3-5
              </button>
              <button
                onClick={() => setTab("411")}
                data-testid="gps-viewer-tab-411"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
                  tab === "411" ? "border-[#E50000] text-white" : "border-transparent text-[#737373] hover:text-white"
                }`}
              >
                <CalendarIcon className="w-4 h-4" /> 4-1-1
              </button>
              <button
                onClick={() => setTab("perf")}
                data-testid="gps-viewer-tab-perf"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px ${
                  tab === "perf" ? "border-[#E50000] text-white" : "border-transparent text-[#737373] hover:text-white"
                }`}
              >
                <Gauge className="w-4 h-4" /> Desvios
              </button>
            </div>
            <div className="p-5 flex-1 overflow-y-auto">
              {tab === "135" ? <View135RO doc={data.one_three_five} />
                : tab === "411" ? <View411RO doc={data.four_one_one} />
                  : <PerformanceGaps playerId={playerId} year={year} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function View135RO({ doc }) {
  if (!doc || !doc.data) {
    return <div className="text-sm text-[#737373] italic" data-testid="gps-viewer-135-empty">Ainda sem GPS 1-3-5 preenchido para este ano.</div>;
  }
  const d = doc.data;
  // Normalize priorities: modern schema is [{name, strategies:[...]}], legacy was [string, ...]
  const priorities = (d.priorities || []).map((p) =>
    typeof p === "string"
      ? { name: p, strategies: [] }
      : { name: p?.name || "", strategies: p?.strategies || [] }
  );
  // Legacy top-level flat strategies (fallback for very old GPS docs)
  const legacyStrategies = Array.isArray(d.strategies) ? d.strategies.filter((s) => (s || "").trim()) : [];
  const hasAny = (d.big_goal || "").trim() || priorities.some((p) => p.name.trim() || (p.strategies || []).some((s) => (s || "").trim())) || legacyStrategies.length > 0;
  if (!hasAny) return <div className="text-sm text-[#737373] italic">Sem conteúdo preenchido.</div>;
  return (
    <div className="space-y-6" data-testid="gps-viewer-135">
      <div className="border-2 border-[#E50000] p-5 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E50000]/20 to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-4 h-4 text-[#E50000]" />
            <div className="kw-overline text-[#E50000]">1 · Grande Objetivo do Ano · Faturação em €</div>
          </div>
          <div className="font-display text-3xl md:text-4xl font-black leading-tight">
            {d.big_goal_amount_eur ? (
              <>€{Number(d.big_goal_amount_eur).toLocaleString("pt-PT")}</>
            ) : (
              <span className="italic normal-case text-sm text-[#737373]">— sem meta anual definida —</span>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[#EAB308]" />
          <div className="kw-overline text-[#EAB308]">3 · Prioridades · 5 Estratégias cada</div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {priorities.slice(0, 3).map((p, i) => (
            <div key={i} className="border border-[#333] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#EAB308] font-bold mb-1">P{i + 1}</div>
              <div className="text-sm font-semibold mb-2">
                {p.name || <span className="text-[#737373] italic font-normal">—</span>}
              </div>
              <div className="space-y-1.5">
                {(p.strategies || []).filter((s) => (s || "").trim()).length === 0 ? (
                  <div className="text-[10px] text-[#555] italic">Sem estratégias</div>
                ) : (
                  (p.strategies || []).map((s, j) => {
                    if (!(s || "").trim()) return null;
                    return (
                      <div key={j} className="flex items-start gap-1.5">
                        <Star className="w-2.5 h-2.5 text-[#22C55E] flex-shrink-0 mt-1" />
                        <div className="text-xs leading-snug text-[#D4D4D4]">{s}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {legacyStrategies.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#22C55E]" />
            <div className="kw-overline text-[#22C55E]">5 · Estratégias (legado)</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            {legacyStrategies.map((s, i) => (
              <div key={i} className="border border-[#333] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold mb-1 inline-flex items-center gap-1">
                  <Star className="w-3 h-3" /> {i + 1}
                </div>
                <div className="text-xs leading-snug">{s}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function View411RO({ doc }) {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  if (!doc || !doc.data) {
    return <div className="text-sm text-[#737373] italic" data-testid="gps-viewer-411-empty">Ainda sem GPS 4-1-1 preenchido para este ano.</div>;
  }
  const d = doc.data;
  const monthPct = (m) => {
    if (!m?.weeks) return 0;
    let total = 0, done = 0;
    m.weeks.forEach((w) => {
      (w.commitments || []).forEach((c, i) => {
        if ((c || "").trim()) { total += 1; if ((w.done || [])[i]) done += 1; }
      });
    });
    return total > 0 ? Math.round((100 * done) / total) : 0;
  };
  const current = (d.months || [])[selectedMonth] || { weeks: [] };
  return (
    <div className="space-y-5" data-testid="gps-viewer-411">
      <div className="border-2 border-[#3B82F6] p-4 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/10 to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-[#3B82F6]" />
            <div className="kw-overline text-[#3B82F6]">Objetivo Anual · Sincronizado com o 1-3-5</div>
          </div>
          <div className="font-display text-3xl md:text-4xl font-black leading-tight">
            {d.annual_goal_amount_eur ? (
              <>€{Number(d.annual_goal_amount_eur).toLocaleString("pt-PT")}</>
            ) : (
              <span className="italic normal-case text-sm text-[#737373]">— sem meta anual definida —</span>
            )}
          </div>
          {d.annual_goal_amount_eur ? (
            <div className="text-xs text-[#A3A3A3] mt-2">
              Target médio mensal: <span className="text-white font-mono">€{Math.round(Number(d.annual_goal_amount_eur) / 12).toLocaleString("pt-PT")}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
        {MONTH_LABELS.map((label, i) => (
          <button key={i}
            onClick={() => setSelectedMonth(i)}
            className={`p-2 border text-left ${selectedMonth === i ? "border-[#E50000] bg-[#E50000]/10" : "border-[#222] hover:border-[#444]"}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold">{label.slice(0, 3)}</div>
            <div className="text-[10px] text-[#737373]">{monthPct((d.months || [])[i])}%</div>
          </button>
        ))}
      </div>
      <div className="border border-[#333] p-4">
        <h3 className="font-display text-lg font-black uppercase mb-2">{MONTH_LABELS[selectedMonth]}</h3>
        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-1">Objetivo do mês</div>
        <div className="text-sm font-semibold mb-3">
          {current.monthly_goal || <span className="text-[#737373] italic font-normal">—</span>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {(current.weeks || []).slice(0, 5).map((week, wIdx) => {
            const commits = week.commitments || [];
            const done = week.done || [];
            const total = commits.filter((c) => (c || "").trim()).length;
            const doneCount = commits.reduce((acc, c, i) => acc + (((c || "").trim() && done[i]) ? 1 : 0), 0);
            return (
              <div key={wIdx} className="border border-[#222] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#EAB308] font-bold">Semana {wIdx + 1}</div>
                  <div className="text-[10px] text-[#737373]">{doneCount}/{total || 0}</div>
                </div>
                <div className="space-y-1.5">
                  {commits.filter((c) => (c || "").trim()).length === 0 && (
                    <div className="text-[10px] text-[#555] italic">—</div>
                  )}
                  {commits.map((c, i) => {
                    if (!(c || "").trim()) return null;
                    return (
                      <div key={i} className="flex items-start gap-2">
                        {done[i] ? <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E] flex-shrink-0 mt-0.5" /> : <Circle className="w-3.5 h-3.5 text-[#666] flex-shrink-0 mt-0.5" />}
                        <div className={`text-xs leading-snug flex-1 ${done[i] ? "line-through text-[#666]" : ""}`}>{c}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
